import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { motion } from 'framer-motion';
import { CERTIFICATIONS, CrafterTypeBadge, CertificationBadge, RarifexVerifiedBadgeFull, Tricolore } from '../components/MadeInItaly';
import { 
  User, 
  Camera, 
  MapPin, 
  Mail, 
  Phone,
  Shield,
  Bell,
  Palette,
  Calendar,
  Languages,
  Award,
  Save,
  Loader2,
  CheckCircle2,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { ImageUploader } from '../components/ImageUploader';

const CATEGORIES = [
  { value: 'ceramica', label: 'Ceramica' },
  { value: 'vetro', label: 'Vetro Soffiato' },
  { value: 'gioielli', label: 'Gioielli' },
  { value: 'tessuti', label: 'Tessuti & Moda' },
  { value: 'legno', label: 'Lavorazione Legno' },
  { value: 'pelle', label: 'Pelletteria' },
  { value: 'carta', label: 'Carta & Stampa' },
  { value: 'metalli', label: 'Metalli' },
  { value: 'food', label: 'Food & Bevande' },
  { value: 'altro', label: 'Altro' }
];

const LANGUAGES_LIST = [
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' }
];

const ITALIAN_REGIONS = [
  'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
  'Friuli Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
  'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
  'Trentino-Alto Adige', 'Umbria', "Valle d'Aosta", 'Veneto'
];

export default function ProfilePage() {
  const [delOpen, setDelOpen] = useState(false);
  const [delInfo, setDelInfo] = useState(null);
  const [delConfirm, setDelConfirm] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const API_DEL = process.env.REACT_APP_BACKEND_URL;

  const openDeleteDialog = async () => {
    setDelConfirm(''); setDelInfo(null); setDelOpen(true);
    try {
      const { data } = await supabase.auth.getSession();
      const res = await fetch(`${API_DEL}/api/account/deletion-eligibility`, {
        headers: { Authorization: `Bearer ${data?.session?.access_token}` },
      });
      setDelInfo(await res.json());
    } catch { setDelInfo({ error: true }); }
  };

  const confirmDelete = async () => {
    setDelBusy(true);
    try {
      const { data } = await supabase.auth.getSession();
      const res = await fetch(`${API_DEL}/api/account/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data?.session?.access_token}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Errore');
      toast.success('Account disattivato');
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (e) { toast.error(e.message); setDelBusy(false); }
  };

  const { user, profile, refreshProfile } = useAuth();

  // Sync preferenza tracciamento dal profilo quando disponibile
  useEffect(() => {
    if (profile && typeof profile.tracking_enabled === 'boolean') {
      setTrackingEnabled(profile.tracking_enabled);
      try { localStorage.setItem('rarifex_tracking_optout', profile.tracking_enabled ? 'false' : 'true'); } catch {}
    }
  }, [profile?.tracking_enabled]);

  const handleToggleTracking = async (v) => {
    setTrackingEnabled(v);
    try { localStorage.setItem('rarifex_tracking_optout', v ? 'false' : 'true'); } catch {}
    if (user?.id) {
      try {
        await fetch(`${API_DEL}/api/me/tracking-preference`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.id, enabled: v }),
        });
      } catch {}
    }
  };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [trackingEnabled, setTrackingEnabled] = useState(() => {
    try { return localStorage.getItem('rarifex_tracking_optout') !== 'true'; } catch { return true; }
  });
  
  // Form state
  const [formData, setFormData] = useState({
    // Personal
    full_name: '',
    avatar_url: '',
    bio: '',
    phone: '',
    
    // Location
    city: '',
    region: '',
    country: 'Italia',
    
    // Crafter specific
    storefront_title: '',
    years_experience: '',
    categories: [],
    languages: ['it'],
    crafter_type: 'hobbista',
    certifications: [],
    partita_iva: '',
    
    // Preferences
    email_notifications: true,
    show_location: true,
    show_phone: false
  });

  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        full_name: profile.full_name || '',
        avatar_url: profile.avatar_url || '',
        bio: profile.bio || '',
        phone: profile.phone || '',
        city: profile.city || '',
        region: profile.region || '',
        country: profile.country || 'Italia',
        storefront_title: profile.storefront_title || '',
        years_experience: profile.years_experience || '',
        categories: profile.categories || [],
        languages: profile.languages || ['it'],
        crafter_type: profile.crafter_type || 'hobbista',
        certifications: profile.certifications || [],
        partita_iva: profile.partita_iva || '',
        email_notifications: profile.email_notifications !== false,
        show_location: profile.show_location !== false,
        show_phone: profile.show_phone || false
      }));
    }
  }, [profile]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => {
      const current = prev.categories || [];
      if (current.includes(category)) {
        return { ...prev, categories: current.filter(c => c !== category) };
      } else if (current.length < 3) {
        return { ...prev, categories: [...current, category] };
      }
      return prev;
    });
  };

  const handleLanguageToggle = (lang) => {
    setFormData(prev => {
      const current = prev.languages || [];
      if (current.includes(lang)) {
        if (current.length > 1) {
          return { ...prev, languages: current.filter(l => l !== lang) };
        }
        return prev;
      }
      return { ...prev, languages: [...current, lang] };
    });
  };

  const handleCertificationToggle = (cert) => {
    setFormData(prev => {
      const current = prev.certifications || [];
      if (current.includes(cert)) {
        return { ...prev, certifications: current.filter(c => c !== cert) };
      }
      return { ...prev, certifications: [...current, cert] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        full_name: formData.full_name,
        avatar_url: formData.avatar_url,
        bio: formData.bio,
        phone: formData.phone,
        city: formData.city,
        region: formData.region,
        country: formData.country,
        storefront_title: formData.storefront_title,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
        categories: formData.categories,
        languages: formData.languages,
        crafter_type: formData.crafter_type,
        certifications: formData.certifications,
        partita_iva: formData.partita_iva || null,
        email_notifications: formData.email_notifications,
        show_location: formData.show_location,
        show_phone: formData.show_phone,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) throw error;
      
      if (refreshProfile) {
        await refreshProfile();
      }
      
      toast.success('Profilo aggiornato con successo!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Errore nel salvataggio: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Accedi per vedere il tuo profilo</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-slate-800">Il Mio Profilo</h1>
          <p className="text-slate-600 mt-1">Gestisci le tue informazioni personali e preferenze</p>
        </motion.div>

        {/* Profile Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Avatar */}
              <div className="relative group">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-[#008080] to-[#006666] flex items-center justify-center">
                  {formData.avatar_url ? (
                    <img 
                      src={formData.avatar_url} 
                      alt="Avatar" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-4xl font-bold">
                      {(formData.full_name || user.email || 'U')[0].toUpperCase()}
                    </span>
                  )}
                </div>
                <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  <Camera className="w-8 h-8 text-white" />
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Immagine troppo grande (max 5MB)');
                        return;
                      }
                      
                      try {
                        const fileExt = file.name.split('.').pop();
                        const fileName = `${user.id}-avatar-${Date.now()}.${fileExt}`;
                        
                        const { error: uploadError } = await supabase.storage
                          .from('avatars')
                          .upload(fileName, file, { upsert: true });
                        
                        if (uploadError) throw uploadError;
                        
                        const { data: { publicUrl } } = supabase.storage
                          .from('avatars')
                          .getPublicUrl(fileName);
                        
                        handleChange('avatar_url', publicUrl);
                        toast.success('Foto caricata! Ricorda di salvare.');
                      } catch (error) {
                        console.error('Upload error:', error);
                        toast.error('Errore nel caricamento');
                      }
                    }}
                  />
                </label>
              </div>

              {/* Quick Info */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold text-slate-800">
                  {formData.full_name || 'Nome non impostato'}
                </h2>
                <p className="text-slate-500 flex items-center justify-center md:justify-start gap-1 mt-1">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </p>
                {formData.city && (
                  <p className="text-slate-500 flex items-center justify-center md:justify-start gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {formData.city}{formData.region ? `, ${formData.region}` : ''}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
                  {profile?.is_crafter_enabled && (
                    <Badge className="bg-[#DAA520] text-white">Artigiano</Badge>
                  )}
                  {profile?.tier === 'premium' && (
                    <Badge className="bg-purple-500 text-white">Premium</Badge>
                  )}
                  {profile?.tier === 'pro' && (
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">Pro</Badge>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#008080] text-white hover:bg-[#006666]"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salva Modifiche
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="personal">
                <User className="w-4 h-4 mr-1" />
                Personale
              </TabsTrigger>
              {profile?.is_crafter_enabled && (
                <TabsTrigger value="crafter">
                  <Palette className="w-4 h-4 mr-1" />
                  Artigiano
                </TabsTrigger>
              )}
              <TabsTrigger value="privacy">
                <Shield className="w-4 h-4 mr-1" />
                Privacy
              </TabsTrigger>
            </TabsList>

            {/* Personal Info Tab */}
            <TabsContent value="personal">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-[#008080]" />
                    Informazioni Base
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Nome Completo *</Label>
                      <Input
                        value={formData.full_name}
                        onChange={(e) => handleChange('full_name', e.target.value)}
                        placeholder="Es: Mario Rossi"
                        data-testid="input-full-name"
                      />
                    </div>
                    <div>
                      <Label>Telefono</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="+39 333 1234567"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <Label>Bio</Label>
                      <Textarea
                        value={formData.bio}
                        onChange={(e) => handleChange('bio', e.target.value)}
                        placeholder="Racconta qualcosa di te..."
                        rows={4}
                        maxLength={500}
                        data-testid="input-bio"
                      />
                      <p className="text-xs text-slate-400 mt-1">{formData.bio.length}/500 caratteri</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#008080]" />
                    Posizione
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Città</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        placeholder="Es: Milano"
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <Label>Regione</Label>
                      <Select 
                        value={formData.region} 
                        onValueChange={(v) => handleChange('region', v)}
                      >
                        <SelectTrigger data-testid="select-region">
                          <SelectValue placeholder="Seleziona regione" />
                        </SelectTrigger>
                        <SelectContent>
                          {ITALIAN_REGIONS.map(region => (
                            <SelectItem key={region} value={region}>{region}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Paese</Label>
                      <Input
                        value={formData.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                        placeholder="Italia"
                        data-testid="input-country"
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* Crafter Tab */}
            {profile?.is_crafter_enabled && (
              <TabsContent value="crafter">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Tipo Artigiano */}
                  <Card className="p-6 md:col-span-2 bg-gradient-to-r from-green-50 via-white to-red-50 border-2">
                    <div className="flex items-center gap-2 mb-4">
                      <Tricolore size="lg" />
                      <h3 className="text-lg font-semibold">Tipo di Artigiano</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => handleChange('crafter_type', 'hobbista')}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          formData.crafter_type === 'hobbista' 
                            ? 'border-[#008080] bg-[#008080]/5' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Award className="w-5 h-5 text-slate-600" />
                          <span className="font-semibold">Hobbista</span>
                        </div>
                        <p className="text-sm text-slate-500">Crei per passione, senza partita IVA. Ideale per chi vende occasionalmente le proprie creazioni.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChange('crafter_type', 'professionista')}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          formData.crafter_type === 'professionista' 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-5 h-5 text-amber-600" />
                          <span className="font-semibold text-amber-700">Artigiano Professionista</span>
                        </div>
                        <p className="text-sm text-slate-500">Hai partita IVA e lavori come artigiano professionista. Badge speciale e visibilità premium.</p>
                      </button>
                    </div>
                    
                    {formData.crafter_type === 'professionista' && (
                      <div className="mt-4 p-4 bg-amber-50 rounded-lg">
                        <Label>Partita IVA</Label>
                        <Input
                          value={formData.partita_iva}
                          onChange={(e) => handleChange('partita_iva', e.target.value)}
                          placeholder="IT12345678901"
                          className="mt-1"
                        />
                      </div>
                    )}
                  </Card>

                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Palette className="w-5 h-5 text-[#DAA520]" />
                      Dettagli Vetrina
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Nome Vetrina *</Label>
                        <Input
                          value={formData.storefront_title}
                          onChange={(e) => handleChange('storefront_title', e.target.value)}
                          placeholder="Es: Ceramiche di Mario"
                          data-testid="input-storefront-title"
                        />
                        <p className="text-xs text-slate-400 mt-1">Questo nome appare nella tua vetrina pubblica</p>
                      </div>
                      <div>
                        <Label className="flex items-center gap-2">
                          <Award className="w-4 h-4" />
                          Anni di Esperienza
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          value={formData.years_experience}
                          onChange={(e) => handleChange('years_experience', e.target.value)}
                          placeholder="Es: 5"
                          data-testid="input-experience"
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Languages className="w-5 h-5 text-[#DAA520]" />
                      Lingue Parlate
                    </h3>
                    <p className="text-sm text-slate-500 mb-3">Seleziona le lingue in cui puoi comunicare</p>
                    <div className="flex flex-wrap gap-2">
                      {LANGUAGES_LIST.map(lang => (
                        <button
                          key={lang.value}
                          type="button"
                          onClick={() => handleLanguageToggle(lang.value)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            formData.languages?.includes(lang.value)
                              ? 'bg-[#008080] text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {lang.label}
                          {formData.languages?.includes(lang.value) && (
                            <CheckCircle2 className="w-3 h-3 ml-1 inline" />
                          )}
                        </button>
                      ))}
                    </div>
                  </Card>

                  {/* Certificazioni Made in Italy */}
                  <Card className="p-6 md:col-span-2">
                    <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                      <Tricolore size="md" />
                      Certificazioni Made in Italy
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">Seleziona le certificazioni che possiedi (verranno mostrate sul tuo profilo)</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(CERTIFICATIONS).map(([key, cert]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleCertificationToggle(key)}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            formData.certifications?.includes(key)
                              ? 'border-green-500 bg-green-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{cert.icon}</span>
                            <span className="font-medium text-sm">{cert.label}</span>
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-2">{cert.description}</p>
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-3">
                      Nota: Le certificazioni verranno verificate dal team Rarifex. Badge "Verificato Rarifex" disponibile dopo la verifica.
                    </p>
                  </Card>

                  <Card className="p-6 md:col-span-2">
                    <h3 className="text-lg font-semibold mb-4">Categorie di Specializzazione</h3>
                    <p className="text-sm text-slate-500 mb-3">Seleziona fino a 3 categorie (verranno usate per il matching con le richieste)</p>
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.map(cat => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => handleCategoryToggle(cat.value)}
                          disabled={!formData.categories?.includes(cat.value) && formData.categories?.length >= 3}
                          className={`px-4 py-2 rounded-lg text-sm transition-all ${
                            formData.categories?.includes(cat.value)
                              ? 'bg-[#DAA520] text-white'
                              : formData.categories?.length >= 3
                                ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`category-${cat.value}`}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-3">
                      {formData.categories?.length || 0}/3 categorie selezionate
                    </p>
                  </Card>
                </div>
              </TabsContent>
            )}

            {/* Privacy Tab */}
            <TabsContent value="privacy">
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#008080]" />
                  Impostazioni Privacy
                </h3>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Personalizzazione e consigli</p>
                      <p className="text-sm text-slate-500">Usiamo la tua navigazione per mostrarti prodotti più affini in home e nelle categorie. Disattivando, vedrai un ordine neutro e non traccieremo la tua attività.</p>
                    </div>
                    <Switch
                      checked={trackingEnabled}
                      onCheckedChange={handleToggleTracking}
                      data-testid="switch-personalized-tracking"
                    />
                  </div>

                  <div className="border-t pt-6 flex items-center justify-between">
                    <div>
                      <p className="font-medium">Notifiche Email</p>
                      <p className="text-sm text-slate-500">Ricevi aggiornamenti su ordini e messaggi</p>
                    </div>
                    <Switch
                      checked={formData.email_notifications}
                      onCheckedChange={(v) => handleChange('email_notifications', v)}
                      data-testid="switch-email-notifications"
                    />
                  </div>
                  
                  <div className="border-t pt-6 flex items-center justify-between">
                    <div>
                      <p className="font-medium">Mostra Posizione</p>
                      <p className="text-sm text-slate-500">La tua città sarà visibile nel profilo pubblico</p>
                    </div>
                    <Switch
                      checked={formData.show_location}
                      onCheckedChange={(v) => handleChange('show_location', v)}
                      data-testid="switch-show-location"
                    />
                  </div>
                  
                  <div className="border-t pt-6 flex items-center justify-between">
                    <div>
                      <p className="font-medium">Mostra Telefono</p>
                      <p className="text-sm text-slate-500">Il tuo numero sarà visibile agli altri utenti</p>
                    </div>
                    <Switch
                      checked={formData.show_phone}
                      onCheckedChange={(v) => handleChange('show_phone', v)}
                      data-testid="switch-show-phone"
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6 mt-6 border-red-200 bg-red-50">
                <h3 className="text-lg font-semibold mb-4 text-red-700">Zona Pericolosa</h3>
                <p className="text-sm text-red-600 mb-4">
                  Queste azioni sono irreversibili. Procedi con cautela.
                </p>
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-100" onClick={openDeleteDialog} data-testid="delete-account-button">
                  Elimina Account
                </Button>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {delOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" data-testid="delete-account-dialog">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-red-700 mb-2">Disattiva il tuo account</h3>
            {!delInfo && <p className="text-sm text-slate-500">Verifica in corso…</p>}
            {delInfo && delInfo.error && <p className="text-sm text-red-600">Errore nella verifica. Riprova.</p>}
            {delInfo && !delInfo.error && delInfo.can_delete === false && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Non puoi disattivare l'account finché hai ordini da gestire:
                <br />• Come acquirente: <strong>{delInfo.active_orders?.as_buyer || 0}</strong>
                <br />• Come artigiano: <strong>{delInfo.active_orders?.as_crafter || 0}</strong>
                <br />Completa o gestisci questi ordini e riprova.
              </div>
            )}
            {delInfo && !delInfo.error && delInfo.can_delete && (
              <>
                <p className="text-sm text-slate-600 mb-2">Verranno <strong>disabilitati</strong> (non eliminati):</p>
                <ul className="text-sm text-slate-600 list-disc pl-5 mb-3">
                  {(delInfo.will_be_disabled || []).map((x, i) => <li key={i}>{x}</li>)}
                </ul>
                <p className="text-xs text-slate-500 mb-4">{delInfo.restore_info}</p>
                <label className="text-sm font-medium text-slate-700">Scrivi <strong>ELIMINA</strong> per confermare</label>
                <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)} className="mt-1 mb-4" data-testid="delete-account-confirm-input" placeholder="ELIMINA" />
              </>
            )}
            <div className="flex gap-3 justify-end mt-2">
              <Button variant="outline" onClick={() => setDelOpen(false)} data-testid="delete-account-cancel">Annulla</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={delBusy || !(delInfo && delInfo.can_delete && delConfirm.trim().toUpperCase() === 'ELIMINA')}
                onClick={confirmDelete}
                data-testid="delete-account-confirm-button"
              >
                {delBusy ? 'Disattivazione…' : 'Disattiva account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
